import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import {
  ESTADO_NAMES,
  HUMAN_LABELS,
  humanize,
  isEmpty,
  type Filters,
  type PropertySort,
} from '../lib/filters';
import { GRADOS_REZAGO, type FacetsResponse, type GradoRezago, type PlaceSuggestion } from '../lib/types';
import { LocationAutocomplete } from './LocationAutocomplete';
import { useAuth } from '../lib/auth';

interface Props {
  filters: Filters;
  onChange: (next: Filters) => void;
  /** Total de properties que matchearían los filtros actuales (cuando facets está cargado). */
  matchedTotal: number | null;
  /** Cuando el user elige un lugar del autocomplete, el FilterBar avisa al padre
   *  con la sugerencia para que dispare el flyTo en el mapa. */
  onSelectPlace?: (place: PlaceSuggestion) => void;
  /** Acciones sobre los resultados (lista, copiar enlace, exportar) — a la derecha del renglón de abajo. */
  actions?: React.ReactNode;
}

const SEARCH_DEBOUNCE_MS = 350;

/**
 * Barra superior con búsqueda + filtros. Sentada sobre el mapa
 * (absolute) para no quitarle real-estate. Se compacta a una sola fila
 * en desktop, wrap en mobile.
 *
 * Pega `onChange(next)` cada vez que algo cambia. La debouncing del
 * search input se hace acá para no inundar al API mientras se escribe.
 */
/** Filtros que viven tras "Más filtros" (los menos usados). */
function secondaryCount(f: Filters): number {
  return [f.status, f.gradoRezago, f.minOpportunityScore, f.minDiscountVsAvaluo].filter((v) => v !== undefined && v !== '').length;
}

/** En celular TODO filtro va tras "Filtros": cuántos hay aplicados. */
function mobileCount(f: Filters): number {
  return secondaryCount(f)
    + [f.cveEnt, f.legalChannel, f.propertyType, f.minPrice ?? f.maxPrice].filter((v) => v !== undefined && v !== '').length;
}

export function FilterBar({ filters, onChange, matchedTotal, onSelectPlace, actions }: Props) {
  const { user } = useAuth();
  const [facets, setFacets] = useState<FacetsResponse | null>(null);
  // Los filtros menos usados van plegados: la barra tenía 11 controles en
  // un renglón que, al partirse en tres, le comía 160 px al mapa. Se abre
  // sola si llega un enlace con alguno de ellos aplicado.
  const [moreOpen, setMoreOpen] = useState(() => secondaryCount(filters) > 0);
  const [searchInput, setSearchInput] = useState(filters.q ?? '');
  const debouncedRef = useRef<number | undefined>(undefined);
  // Snapshot SIEMPRE fresco de filters para el timer del debounce: el
  // closure del setTimeout capturaba los filters de hace 350ms, así que
  // teclear y cambiar un select dentro de esa ventana hacía que el timer
  // escribiera el snapshot viejo y REVIRTIERA el cambio del select.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Re-fetch facets cada vez que filters cambia. Sirve para el "facets
  // restantes" (al filtrar por estado, los counts de channel se ajustan).
  useEffect(() => {
    const ctrl = new AbortController();
    api.facets(filters, ctrl.signal).then(setFacets).catch(() => {});
    return () => ctrl.abort();
  }, [filters]);

  // Si la URL externa cambió (back/forward), reflejamos en el input.
  useEffect(() => {
    setSearchInput(filters.q ?? '');
  }, [filters.q]);

  // El timer pendiente no debe disparar tras desmontar.
  useEffect(() => () => window.clearTimeout(debouncedRef.current), []);

  function applySearchDebounced(value: string) {
    setSearchInput(value);
    window.clearTimeout(debouncedRef.current);
    debouncedRef.current = window.setTimeout(() => {
      const trimmed = value.trim();
      // filtersRef.current, no `filters`: preserva cualquier cambio de
      // filtro hecho DESPUÉS de teclear pero ANTES de que venza el debounce.
      const next: Filters = { ...filtersRef.current };
      if (trimmed.length > 0) next.q = trimmed;
      else delete next.q;
      onChange(next);
    }, SEARCH_DEBOUNCE_MS);
  }

  function setFilter<K extends keyof Filters>(k: K, v: Filters[K]) {
    const next: Filters = { ...filters };
    if (v === undefined || v === '' || v === null || v === 'undefined') delete next[k];
    else next[k] = v;
    onChange(next);
  }

  /**
   * Cuando el user selecciona un lugar del dropdown:
   *   estado    → set cveEnt, clear cveMun, clear q
   *   municipio → set cveEnt + cveMun, clear q
   *   colonia   → set cveEnt + cveMun (si hay) + q (nombre de colonia)
   *
   * En cualquier caso reseteamos el input visible y notificamos al padre
   * para que dispare el flyTo correspondiente.
   */
  function handleSelectPlace(p: PlaceSuggestion) {
    // Cancela cualquier debounce de tecleo pendiente: sin esto, el timer
    // podía disparar DESPUÉS de la selección y pisarla con el texto viejo.
    window.clearTimeout(debouncedRef.current);
    const next: Filters = { ...filters };
    delete next.q;
    delete next.cveMun;
    delete next.cveEnt;
    if (p.cveEnt) next.cveEnt = p.cveEnt;
    if (p.cveMun) next.cveMun = p.cveMun;
    if (p.kind === 'colonia') {
      // Para colonias, additionally usamos el nombre como `q` para
      // matchear properties dentro del bbox que comparten esa colonia.
      next.q = p.name;
    }
    // El input visible lo sincroniza el efecto [filters.q]: '' para
    // estado/municipio (q borrado) y el nombre de la colonia cuando aplica.
    // (El setSearchInput('') manual de antes era pisado por ese efecto.)
    onChange(next);
    onSelectPlace?.(p);
  }

  const empty = isEmpty(filters);

  const nSecondary = secondaryCount(filters);
  const nMobile = mobileCount(filters);

  return (
    <div className={`filter-bar ${moreOpen ? 'more-open' : ''}`}>
      <div className="filter-row filter-row-main">
        <LocationAutocomplete
          value={searchInput}
          onSearch={applySearchDebounced}
          onSelectPlace={handleSelectPlace}
          placeholder="Buscar estado, municipio, colonia o dirección…"
        />

        <Select
          value={filters.cveEnt ?? ''}
          onChange={(v) => setFilter('cveEnt', v || undefined)}
          options={ensureSelected(facets?.byEstado ?? [], filters.cveEnt).map((f) => ({
            value: f.key,
            label: `${ESTADO_NAMES[f.key] ?? f.key}${f.count > 0 ? ` (${f.count.toLocaleString('es-MX')})` : ''}`,
          }))}
          allLabel="Todos los estados"
          className="fb-primary"
        />

        <Select
          value={filters.legalChannel ?? ''}
          onChange={(v) => setFilter('legalChannel', v || undefined)}
          options={ensureSelected(facets?.byChannel ?? [], filters.legalChannel).map((f) => ({
            value: f.key,
            label: `${humanize('channel', f.key)}${f.count > 0 ? ` (${f.count.toLocaleString('es-MX')})` : ''}`,
          }))}
          allLabel="Todos los canales"
          className="fb-primary"
        />

        <Select
          value={filters.propertyType ?? ''}
          onChange={(v) => setFilter('propertyType', v || undefined)}
          options={ensureSelected(facets?.byPropertyType ?? [], filters.propertyType).map((f) => ({
            value: f.key,
            label: `${humanize('propertyType', f.key)}${f.count > 0 ? ` (${f.count.toLocaleString('es-MX')})` : ''}`,
          }))}
          allLabel="Cualquier tipo"
          className="fb-primary"
        />

        <PriceRangeInput
          className="fb-primary"
          min={filters.minPrice}
          max={filters.maxPrice}
          onChange={(min, max) => {
            // Semántica de borrado como el resto de filtros: nada de keys
            // con valor `undefined` dentro del objeto Filters (rompen la
            // igualdad por JSON y son una trampa para serializadores).
            const next: Filters = { ...filters };
            if (min === undefined) delete next.minPrice; else next.minPrice = min;
            if (max === undefined) delete next.maxPrice; else next.maxPrice = max;
            onChange(next);
          }}
        />

        <button
          type="button"
          className={`filter-more-btn ${moreOpen ? 'active' : ''}`}
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((v) => !v)}
          title={moreOpen ? 'Oculta los filtros adicionales' : 'Estado del inmueble, nivel socioeconómico, score y descuento vs avalúo'}
        >
          <span className="fb-label-desktop">{moreOpen ? 'Menos filtros' : 'Más filtros'}{nSecondary > 0 ? ` · ${nSecondary}` : ''}</span>
          <span className="fb-label-mobile">{moreOpen ? 'Ocultar filtros' : 'Filtros'}{nMobile > 0 ? ` · ${nMobile}` : ''}</span>
          <span aria-hidden className="fb-caret">{moreOpen ? '▴' : '▾'}</span>
        </button>

        {!empty && (
          <button className="reset-btn" onClick={() => onChange({})} title="Limpiar filtros">
            Limpiar
          </button>
        )}
      </div>

      {moreOpen && (
        <div className="filter-row filter-row-more">
          {/* Estatus (RFC-0028): sin elegir, sólo lo que sigue en el mercado;
              lo que la fuente dejó de publicar se ve eligiéndolo o con "Todas". */}
          <Select
            value={filters.status ?? ''}
            onChange={(v) => setFilter('status', v || undefined)}
            options={[
              ...ensureSelected((facets?.byStatus ?? []).filter((f) => f.key !== 'ALL'), filters.status === 'ALL' ? undefined : filters.status).map((f) => ({
                value: f.key,
                label: `${humanize('status', f.key)}${f.count > 0 ? ` (${f.count.toLocaleString('es-MX')})` : ''}`,
              })),
              { value: 'ALL', label: 'Todas (incluye las que dejaron de publicarse)' },
            ]}
            allLabel="En el mercado (activas)"
          />

          {/* Nivel socioeconómico (RFC-0022 Fase 2). Opciones fijas y en orden
              de MEJOR a PEOR: la escala CONEVAL va invertida respecto al NSE
              ("Muy bajo" rezago = nivel alto), así que la etiqueta lo traduce.
              Sólo alcanza a propiedades con precisión de colonia o calle. */}
          <Select
            value={filters.gradoRezago ?? ''}
            onChange={(v) => setFilter('gradoRezago', (v || undefined) as GradoRezago | undefined)}
            options={GRADOS_REZAGO.map((g) => ({ value: g, label: `Nivel ${NIVEL_LABELS[g]} (rezago ${g.toLowerCase()})` }))}
            allLabel="Cualquier nivel socioeconómico"
          />

          <OpportunityFilter
            value={filters.minOpportunityScore}
            onChange={(v) => setFilter('minOpportunityScore', v as Filters['minOpportunityScore'])}
          />

          <DiscountAvaluoFilter
            value={filters.minDiscountVsAvaluo}
            onChange={(v) => setFilter('minDiscountVsAvaluo', v)}
          />
        </div>
      )}

      <div className="filter-row filter-row-aux">
        {/* Sin «Top oportunidades» ni filtros de situación jurídica
            (2026-10-08, decisión de Antonio del 30-sep): TerraKairos no
            verifica litigios ni gravámenes, así que no filtra por ellos. */}
        <div className="fb-aux-filters">
          {user && <CreateAlertButton filters={filters} />}
        </div>

        <div
          className="filter-meta"
          title={facets?.priceRange && typeof facets.priceRange.p25 === 'number' && typeof facets.priceRange.p75 === 'number'
            ? `Precio típico (mitad central): ${fmtMoney(facets.priceRange.p25)} – ${fmtMoney(facets.priceRange.p75)}`
            : undefined}
        >
          {matchedTotal !== null ? (
            matchedTotal === 0 && !empty ? (
              // RFC-0021 A4: estado vacío pedagógico — jamás renderizar
              // estadísticas de un set vacío ("precio típico $null – $null").
              <span className="meta-empty">
                Sin coincidencias con estos filtros
                {filters.minDiscountVsAvaluo !== undefined
                  ? ' — pocos listings tienen avalúo registrado; prueba relajando el filtro de descuento.'
                  : ' — prueba quitando alguno.'}
              </span>
            ) : (
              <>
                <strong>{matchedTotal.toLocaleString('es-MX')}</strong>
                {matchedTotal === 1 ? ' propiedad' : ' propiedades'}
                {empty ? ' en total' : matchedTotal === 1 ? ' coincide' : ' coinciden'}
                {facets && facets.priceRange &&
                  typeof facets.priceRange.p25 === 'number' &&
                  typeof facets.priceRange.p75 === 'number' &&
                  filters.minPrice === undefined && filters.maxPrice === undefined && (
                  <span className="meta-aux">
                    · precio típico {fmtMoney(facets.priceRange.p25)} – {fmtMoney(facets.priceRange.p75)}
                  </span>
                )}
              </>
            )
          ) : (
            <span style={{ color: 'var(--text-dim)' }}>cargando…</span>
          )}
        </div>

        {actions && <div className="filter-actions">{actions}</div>}
      </div>
    </div>
  );
}

/**
 * Botón "Crear alerta" (RFC-0019): guarda los filtros ACTUALES con un
 * email y el digest diario avisa de inventario nuevo / recortes de
 * precio que matcheen. Popover inline — sin modal global ni deps.
 */
function CreateAlertButton({ filters }: { filters: Filters }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');
  const [okMsg, setOkMsg] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setState('error');
      setErrMsg('Escribe un email válido.');
      return;
    }
    setState('sending');
    const r: { ok: boolean; error?: string; confirmation?: string } = await api.createAlert(v, filters).catch(() => ({ ok: false, error: 'network' }));
    if (r.ok) {
      // Doble opt-in (RFC-0026): la alerta no manda nada hasta que se
      // confirma desde el correo.
      setOkMsg(
        r.confirmation === 'already_confirmed' ? '✓ Esa alerta ya está activa.'
        : r.confirmation === 'sent' ? '✓ Revisa tu correo y confirma la alerta para empezar a recibirla.'
        : '✓ Alerta registrada. Te llegará un correo para confirmarla.',
      );
      setState('ok');
      setTimeout(() => { setOpen(false); setState('idle'); setEmail(''); }, 4000);
    } else {
      setState('error');
      setErrMsg(
        r.error === 'rate_limited' ? 'Demasiados intentos, espera un minuto.'
        : r.error === 'too_many_alerts' ? 'Ese email ya tiene el máximo de alertas.'
        : 'No se pudo crear la alerta, intenta de nuevo.',
      );
    }
  }

  return (
    <div className="alert-wrap">
      <button
        type="button"
        className={`filter-preset alert-btn ${open ? 'active' : ''}`}
        onClick={() => { setOpen(!open); setState('idle'); }}
        title="Recibe un email diario cuando entre inventario nuevo o haya recortes de precio que coincidan con los filtros actuales"
      >
        ✉ Crear alerta
      </button>
      {open && (
        <form className="alert-popover" onSubmit={submit}>
          {state === 'ok' ? (
            <div className="alert-ok">{okMsg}</div>
          ) : (
            <>
              <div className="alert-caption">
                Te avisamos cuando entre inventario nuevo o baje de precio algo
                que coincida con tus filtros actuales.
              </div>
              <div className="alert-input-row">
                <input
                  type="email"
                  className="alert-input"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={state === 'sending'}
                  autoFocus
                />
                <button type="submit" className="alert-submit" disabled={state === 'sending'}>
                  {state === 'sending' ? '…' : 'Crear'}
                </button>
              </div>
              {state === 'error' && <div className="alert-error">{errMsg}</div>}
            </>
          )}
        </form>
      )}
    </div>
  );
}

/** Traducción de grado de rezago → nivel socioeconómico, para leer derecho. */
const NIVEL_LABELS: Record<GradoRezago, string> = {
  'Muy bajo': 'alto',
  'Bajo': 'medio-alto',
  'Medio': 'medio',
  'Alto': 'medio-bajo',
  'Muy alto': 'bajo',
};

function Select({ value, onChange, options, allLabel, className }: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  allLabel: string;
  className?: string;
}) {
  return (
    <select
      className={`filter-select${className ? ` ${className}` : ''}`}
      aria-label={allLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function PriceRangeInput({ min, max, onChange, className }: {
  className?: string;
  min: number | undefined;
  max: number | undefined;
  onChange: (min: number | undefined, max: number | undefined) => void;
}) {
  const [localMin, setLocalMin] = useState(min !== undefined ? String(min) : '');
  const [localMax, setLocalMax] = useState(max !== undefined ? String(max) : '');
  useEffect(() => setLocalMin(min !== undefined ? String(min) : ''), [min]);
  useEffect(() => setLocalMax(max !== undefined ? String(max) : ''), [max]);

  function commit() {
    const minN = localMin ? Number(localMin) : NaN;
    const maxN = localMax ? Number(localMax) : NaN;
    onChange(
      Number.isFinite(minN) && minN > 0 ? minN : undefined,
      Number.isFinite(maxN) && maxN > 0 ? maxN : undefined,
    );
  }

  return (
    <div className={`price-wrap${className ? ` ${className}` : ''}`}>
      <input
        type="number"
        className="price-input"
        placeholder="$ min"
        value={localMin}
        onChange={(e) => setLocalMin(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
        min="0"
        step="50000"
      />
      <span className="price-dash">–</span>
      <input
        type="number"
        className="price-input"
        placeholder="$ max"
        value={localMax}
        onChange={(e) => setLocalMax(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
        min="0"
        step="50000"
      />
    </div>
  );
}

/**
 * Asegura que el valor actualmente seleccionado siempre aparezca en las
 * opciones del select, incluso si el endpoint /v1/facets no lo trae
 * (porque el filtro restrictivo dejó 0 results en otras dimensiones).
 * Sin esto, el user no puede ver/deseleccionar su filtro actual.
 */
function ensureSelected(facets: { key: string; count: number }[], current: string | undefined): { key: string; count: number }[] {
  if (!current) return facets;
  if (facets.some((f) => f.key === current)) return facets;
  return [{ key: current, count: 0 }, ...facets];
}

function fmtMoney(n: number): string {
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
  return '$' + n;
}

function DiscountAvaluoFilter({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <select
      className="filter-select"
      value={value !== undefined ? String(value) : ''}
      onChange={(e) => {
        const v = e.target.value;
        if (!v) onChange(undefined);
        else onChange(Number(v));
      }}
      title="Descuento mínimo vs avalúo declarado"
    >
      <option value="">Cualquier desc. avalúo</option>
      <option value="15">≥ 15% vs avalúo</option>
      <option value="25">≥ 25% vs avalúo</option>
      <option value="30">≥ 30% vs avalúo</option>
    </select>
  );
}

/** Orden del listado. Vive en la cabecera de la lista (ResultsList), que es donde se nota. */
export function SortFilter({
  value,
  onChange,
}: {
  value: PropertySort | undefined;
  onChange: (v: PropertySort | undefined) => void;
}) {
  return (
    <select
      className="filter-select filter-select-sort"
      value={value ?? 'opportunity'}
      onChange={(e) => {
        const v = e.target.value as PropertySort;
        onChange(v === 'opportunity' ? undefined : v);
      }}
      title="Orden del listado de propiedades"
    >
      <option value="opportunity">Orden: Oportunidad</option>
      <option value="date_updated">Orden: Recientes</option>
      <option value="discount">Orden: % descuento</option>
      <option value="price">Orden: Precio ↑</option>
      <option value="price_desc">Orden: Precio ↓</option>
      <option value="price_drop">Orden: Bajaron de precio</option>
    </select>
  );
}

/**
 * Filtro por opportunity_score mínimo: sólo el umbral numérico, sin
 * adjetivos («Bueno», «Excelente») desde el 2026-10-08. Select simple para
 * no agregar otro slider; el grueso de propiedades cae entre 40-70, así
 * que estos cortes son los que más mueven la aguja.
 */
function OpportunityFilter({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <select
      className="filter-select"
      value={value !== undefined ? String(value) : ''}
      onChange={(e) => {
        const v = e.target.value;
        if (!v) onChange(undefined);
        else onChange(Number(v));
      }}
      title="Filtra por Opportunity Score mínimo (0-100)"
    >
      <option value="">Cualquier score</option>
      <option value="45">Score ≥ 45</option>
      <option value="60">Score ≥ 60</option>
      <option value="75">Score ≥ 75</option>
    </select>
  );
}

// Suprime warning cuando no se renderiza el panel auxiliar.
void HUMAN_LABELS;
